<template>
  <Modal
    v-if="open"
    :model-value="true"
    title="Replace file?"
    size="small"
    @update:model-value="cancel"
  >
    <p class="mb-4 text-gray-700" data-cy="replace-file-body">
      Replacing the file clears the load and the dry run. The mapping is kept
      for columns still in the file.
    </p>
    <DatasetUpload
      :dataset="upload.dataset"
      :file-upload-link="upload.file_upload"
      :hint="hint"
      :max-bytes="MAX_MAPPED_BYTES"
      @uploaded="key = $event"
      @cleared="key = ''"
      @busy="uploading = $event"
    />
    <p
      v-if="error"
      class="mt-2 text-sm text-red-600"
      role="alert"
      data-cy="replace-file-error"
    >
      {{ error }}
    </p>
    <template #footer>
      <div class="flex justify-end gap-2 p-4">
        <SeraButton
          text="Cancel"
          type="secondary"
          data-cy="replace-file-cancel"
          @click="cancel"
        />
        <SeraButton
          text="Replace file"
          :disabled="!key || uploading"
          :saving="replacing"
          data-cy="replace-file-confirm"
          @click="replace"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

import SeraButton from "@/components/Button.vue";
import Modal from "@/components/Seranova/Modal/Modal.vue";
import DatasetUpload from "@/DataImports/flow/DatasetUpload.vue";
import {
  MAX_MAPPED_BYTES,
  normalizeExtensions
} from "@/DataImports/flow/fileExtensions";
import type { DroppedField, ReplaceFileUpload } from "@/DataImports/flow/types";

const props = defineProps<{
  open: boolean;
  upload: ReplaceFileUpload;
  /** `links.replace_file`. */
  url: string;
}>();
const emit = defineEmits<{ replaced: [dropped: DroppedField[]]; cancel: [] }>();

const store = useStore();
const key = ref("");
const uploading = ref(false);
const replacing = ref(false);
const error = ref("");

const hint = computed(
  () =>
    `${normalizeExtensions(props.upload.dataset.extensions).join(" or ")}, up to 50 MB`
);

watch(
  () => props.open,
  (open) => {
    if (!open) return;
    key.value = "";
    error.value = "";
  }
);

async function replace() {
  replacing.value = true;
  error.value = "";
  try {
    const { data } = await store.dispatch("replaceImportFile", {
      url: props.url,
      data: { [`${props.upload.dataset.name}_s3_key`]: key.value }
    });
    emit("replaced", data?.dropped_fields ?? []);
  } catch (failure: any) {
    error.value =
      failure?.response?.data?.message || "The file could not be replaced.";
  } finally {
    replacing.value = false;
  }
}

function cancel() {
  if (!replacing.value) emit("cancel");
}
</script>
